import { Typography } from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  deleteFigmaIntegration,
  FigmaFile,
  getProjectFigmaDetailsFile,
  qk,
} from 'api'
import { env } from 'app/env'
import { Dialog, DialogContent, ToastContent } from 'elements/feedback'
import { LinkButton } from 'elements/navigation'
import { useBoolean } from 'lib/hooks'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { IntegrationItem } from '../integration-item'
import { IntegrationsBlock } from '../integrations-block'
import { CreateFigmaIntegrationForm } from './create-figma-integration-form'
import { FigmaIntegrationDetails } from './figma-integration-details'

type FigmaIntegrationProps = {
  projectId: string
  authCode: string | null
  removeAuthCode: () => void
}

export const FigmaIntegration = ({
  projectId,
  authCode,
  removeAuthCode,
}: FigmaIntegrationProps) => {
  const { t } = useTranslation()

  const { data, refetch } = useQuery(
    qk.projects.figmaFile.toKeyWithArgs({ projectId }),
    () => getProjectFigmaDetailsFile({ projectId }),
  )

  const $deleteFigmaIntegration = useMutation(deleteFigmaIntegration)

  const [selectedIntegration, setSelectedIntegration] =
    useState<FigmaFile | null>(null)

  const deleteDialogOpen = useBoolean()

  const closeDetailsDialog = () => {
    setSelectedIntegration(null)
  }

  const createIntegrationDialogOpen = useBoolean(authCode !== null)

  const closeCreateIntegrationDialog = () => {
    createIntegrationDialogOpen.setFalse()
    removeAuthCode()
  }

  if (!data) return null

  const { figma } = data

  return (
    <>
      <IntegrationsBlock
        variant="FIGMA"
        connectButton={
          figma ? undefined : (
            <LinkButton
              to={`${env.REACT_APP_LIBRARIA_FIGMA_APP}&state=${projectId}`}
              variant="text"
              sx={{ color: 'primary.main' }}
            >
              {t('common.connect')}
            </LinkButton>
          )
        }
        connectedIntegrations={
          figma ? (
            <IntegrationItem
              title={figma.fileName}
              onEdit={() => setSelectedIntegration(figma)}
              onDelete={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                deleteDialogOpen.setTrue()
              }}
            />
          ) : undefined
        }
      />

      <Dialog open={selectedIntegration !== null} onClose={closeDetailsDialog}>
        {selectedIntegration !== null && (
          <FigmaIntegrationDetails
            figmaFile={selectedIntegration}
            onClose={closeDetailsDialog}
          />
        )}
      </Dialog>

      <Dialog
        open={createIntegrationDialogOpen.isTrue}
        onClose={closeCreateIntegrationDialog}
      >
        {authCode && (
          <CreateFigmaIntegrationForm
            projectId={projectId}
            closeDialog={closeCreateIntegrationDialog}
            authCode={authCode}
          />
        )}
      </Dialog>

      <Dialog
        open={deleteDialogOpen.isTrue}
        onClose={deleteDialogOpen.setFalse}
      >
        <DialogContent
          variant="danger"
          title={t('projects.settings.integrations.remove_integration')}
          isActionLoading={$deleteFigmaIntegration.isLoading}
          onConfirm={() => {
            $deleteFigmaIntegration.mutate(
              { projectId },
              {
                onSuccess: () => {
                  deleteDialogOpen.setFalse()
                  refetch()
                  toast.success(
                    <ToastContent
                      title={t(
                        'projects.settings.integrations.figma_integration_successfully_deleted',
                        { fileName: figma?.fileName ?? '' },
                      )}
                    />,
                  )
                },
              },
            )
          }}
          onDeny={deleteDialogOpen.setFalse}
          confirmText={t('common.remove')}
          denyText={t('common.cancel')}
        >
          <Typography variant="caption1" color="text.secondary">
            <Trans
              i18nKey="projects.settings.integrations.delete_integration_confirmation"
              values={{
                integrationName: figma?.fileName ?? '',
              }}
            />
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  )
}
